import {
  DECIMAL_PRECISION,
  DISTANCE_THRESHOLDS,
  INFINITY_SYMBOLS,
  LABEL_COUNT,
  PERCENTAGE_MAX,
  PERCENTAGE_MIN,
  ROUNDING_INCREMENTS,
  ROUNDING_MAGNITUDE_THRESHOLDS,
} from './const';
import type { LabelData } from '.';

export type LabelCountConfig = {
  fixed?: number;
  min?: number;
  max?: number;
  base?: number;
  scaleFactor?: number;
};

export const calculatePositionPercentage = (
  value: number,
  min: number,
  max: number
): number => {
  const percentage = ((value - min) / (max - min)) * PERCENTAGE_MAX;
  return Math.max(PERCENTAGE_MIN, Math.min(PERCENTAGE_MAX, percentage));
};

export const isInfinitySymbol = (label: string): boolean => {
  return INFINITY_SYMBOLS.some(
    symbol => label === symbol || label.toLowerCase() === symbol
  );
};

export const parseStepOptionValue = (
  label: string,
  index: number,
  totalOptions: number,
  min: number,
  max: number
): number => {
  if (isInfinitySymbol(label)) {
    return max;
  }

  const parsed = parseFloat(label);
  if (isNaN(parsed)) {
    return min + (index / (totalOptions - 1)) * (max - min);
  }

  return parsed;
};

export const calculateOrderOfMagnitude = (range: number): number => {
  return range > 0 ? Math.floor(Math.log10(range)) : 0;
};

export const calculateRoundingIncrement = (magnitude: number): number => {
  if (magnitude >= ROUNDING_MAGNITUDE_THRESHOLDS.VERY_LARGE) {
    return ROUNDING_INCREMENTS.VERY_LARGE;
  }
  if (magnitude >= ROUNDING_MAGNITUDE_THRESHOLDS.MEDIUM) {
    return magnitude >= ROUNDING_MAGNITUDE_THRESHOLDS.LARGE
      ? ROUNDING_INCREMENTS.LARGE
      : ROUNDING_INCREMENTS.MEDIUM;
  }
  if (magnitude >= ROUNDING_MAGNITUDE_THRESHOLDS.SMALL) {
    return ROUNDING_INCREMENTS.SMALL;
  }
  if (magnitude >= ROUNDING_MAGNITUDE_THRESHOLDS.TINY) {
    return ROUNDING_INCREMENTS.TINY;
  }
  return ROUNDING_INCREMENTS.MINIMAL;
};

export const calculateTargetLabelCount = (
  range: number,
  magnitude: number,
  config?: LabelCountConfig
): number => {
  if (config?.fixed !== undefined) {
    return config.fixed;
  }

  const base = config?.base ?? LABEL_COUNT.BASE;
  const scaleFactor = config?.scaleFactor ?? LABEL_COUNT.SCALE_FACTOR;
  const min = config?.min ?? LABEL_COUNT.MIN;
  const max = config?.max ?? LABEL_COUNT.MAX;

  const calculatedCount = Math.floor(
    base + Math.sqrt(range / magnitude) * scaleFactor
  );
  return Math.min(max, Math.max(min, calculatedCount));
};

export const getMinDistanceThreshold = (range: number): number => {
  return range > DISTANCE_THRESHOLDS.LARGE_RANGE_BOUNDARY
    ? DISTANCE_THRESHOLDS.LARGE_RANGE
    : DISTANCE_THRESHOLDS.SMALL_RANGE;
};

export const formatDisplayValue = (value: number): number => {
  return Number.isInteger(value)
    ? value
    : Math.round(value * DECIMAL_PRECISION) / DECIMAL_PRECISION;
};

export const roundToIncrement = (value: number, increment: number): number => {
  return Math.round(value / increment) * increment;
};

export const isLabelTooClose = (
  position: number,
  existingLabels: LabelData[],
  threshold: number
): boolean => {
  return existingLabels.some(
    existing => Math.abs(existing.position - position) < threshold
  );
};
